<template>
  <PageHeader image="header-1" :title="$lang('title')" :breadcrumbs="$lang('breadcr')" withMobile />
  <!-- section1 -->
  <section class="py-8 bg-blue4">
    <div class="container">
      <h2 class="fw-bold text-blue2 mb-3">
        {{ $lang("section1.title1") }}
      </h2>
      <h3 class="fw-semibold text-black2 mb-5">
        {{ $lang("section1.subtitle1") }}
      </h3>
      <h2 class="fw-bold text-green1 mb-3">
        {{ $lang("section1.title2") }}
      </h2>
      <p class="text-black2 mb-6 course-intro-text">{{ $lang('section1.text1') }} <strong> {{ $lang('section1.text2') }}</strong> {{ $lang('section1.text3') }} <strong> {{ $lang('section1.text4') }}</strong> {{ $lang('section1.text5') }} <strong> {{ $lang('section1.text6') }}</strong></p>

      <!-- Single column layout with enhanced styling -->
      <div class="course-cards-container">
        <div class="mb-5" v-for="(c, i) of $lang('section1.cards')" :key="i">
          <CardTag
            class="h-100 m-0 course-card-enhanced"
            :tag="c.tag"
            :title="c.title"
            :color="c.color"
            :button="c.button"
            buttonLink="contact_us"
            :withHeart="i === 0"
            :pricing="c.pricing"
            :generalPricing="c.generalPricing"
            :pricingTable="c.pricingTable"
            @click="handleCardClick(c)"
          >
            <div v-if="c.bodyTitle !== ''"><p class="course-title"><b>{{ c.bodyTitle }}</b></p></div>
            <div><p class="course-description" v-html="c.body"></p></div>
            <div v-if="c.details">
              <ul class="course-details-list">
                <li v-for="(detail, idx) in c.details" :key="idx">
                  <strong>{{ detail.label }}:</strong> 
                  <span v-if="!detail.isSchedule">{{ detail.value }}</span>
                  <ul v-else>
                    <li v-for="(schedule, scheduleIdx) in detail.schedules" :key="scheduleIdx">
                      {{ schedule }}
                    </li>
                  </ul>
                </li>
              </ul>
            </div>
            <ImageContainer
              :fileName="c.image"
              v-if="c.image"
              background
              contain
              size="30px"
              class="mt-3 mb-4"
            />
          </CardTag>
        </div>
      </div>
    </div>
  </section>
  <ImageContainer background size="600px" fileName="section-header-1" />
  <!-- section2 -->
  <section class="py-8 bg-blue2">
    <div class="container">
      <div class="row justify-content-center text-center mb-6">
        <div class="col-lg-9">
          <h2 class="fw-bold text-green3 mb-3">
            {{ $lang("section2.title") }}
          </h2>
          <p class="section-text">{{ $lang("section2.text1") }}</p>
        </div>
      </div>
      <div class="d-flex justify-content-between mb-2 mx-4">
        <h5 class="fw-bold text-green3">{{ $lang("section2.text2") }}</h5>
        <h5 class="fw-bold text-green3 text-right">
          {{ $lang("section2.text3") }}
        </h5>
      </div>

      <Accordion :data="$lang('section2.accordion_data')" class="mb-5">
        <template #head="{ title, tag, onClick }">
          <div class="accordion-head accordion-head-tag" @click="onClick">
            <h4>{{ title }}</h4>

            <div class="tag">
              {{ tag }}
              <i class="fa fa-plus"></i>
            </div>
          </div>
        </template>
      </Accordion>

      <div class="text-center">
        <p class="section-text">{{ $lang("section2.text4") }}</p>
        <h3 class="fw-bold text-green2 mb-3">{{ $lang("section2.text5") }}</h3>
        <p class="section-text">{{ $lang("section2.text6") }}</p>
        <button class="btn btn-green-grad px-5" @click="goTo('contact_us')">
          {{ $lang("section2.button") }}
        </button>
      </div>
    </div>
  </section>
  <!-- section3 -->
  <section class="pt-8 pb-10 bg-blue3">
    <div class="container">
      <div class="row align-items-center">
        <div class="col-lg-5 offset-lg-1 order-lg-1 mb-5 mb-lg-0">
          <h2 class="fw-bold text-blue2 mb-4">{{ $lang("section3.title") }}</h2>
          <p class="text-white section-text">{{ $lang("section3.text") }}</p>
        </div>
        <div class="col-lg-6">
          <div class="d-grid grid-2-cols gap-4">
            <div class="card card-white m-0">
              <div class="card-body">
                <ImageContainer background contain size="70px" fileName="logo_cambridge_1" />
              </div>
            </div>
            <div class="card card-white m-0">
              <div class="card-body">
                <ImageContainer background contain size="70px" fileName="logo_tie_1" />
              </div>
            </div>
            <div class="card card-white m-0">
              <div class="card-body h-100">
                <ImageContainer
                  background
                  contain
                  size="50px"
                  class="mt-2"
                  fileName="logo_ielts_1"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import PageHeader from "@/components/PageHeader";
import CardTag from "@/components/CardTag";
import Accordion from "@/components/Accordion.vue";
import { trackEvent, trackCustomEvent, StandardEvents } from "@/utils/facebookPixel";

export default {
  components: {
    PageHeader,
    CardTag,
    Accordion,
  },
  mounted() {
    // Track ViewContent event for course page
    trackEvent(StandardEvents.VIEW_CONTENT, {
      content_name: 'Courses Page',
      content_category: 'Course Information',
      content_type: 'product_group'
    });
  },
  methods: {
    goTo(route) {
      // Track course interest when clicking contact us
      trackEvent(StandardEvents.LEAD, {
        content_name: 'Course Interest',
        content_category: 'Courses'
      });
      this.$router.push({ name: route });
    },
    handleCardClick(course) {
      // Track clicks on specific course cards
      trackCustomEvent('CourseCardClick', {
        course_name: course.title,
        course_duration: course.tag
      });
    }
  }
};
</script>

<style>
/* Increased font sizes */
.course-intro-text {
  font-size: 1.1rem;
  line-height: 1.6;
}

.course-title {
  font-size: 1.35rem;
  margin-bottom: 1.2rem;
  color: var(--blue2);
}

.course-description {
  font-size: 1.1rem;
  line-height: 1.7;
  margin-bottom: 1.5rem;
}

.section-text {
  font-size: 1.1rem;
  line-height: 1.6;
}

/* Course cards container */
.course-cards-container {
  max-width: 900px;
  margin: 0 auto;
}

/* Enhanced course card */
.course-card-enhanced {
  transition: all 0.3s ease;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 6px 18px rgba(0,0,0,0.1);
}

.course-card-enhanced:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 24px rgba(0,0,0,0.15);
}

.course-card-enhanced .card-header {
  padding: 1.5rem 1.5rem 1.2rem;
}

.course-card-enhanced .card-body {
  padding: 1.5rem;
}

/* Improved course details list with iconized bullet points */
.course-details-list {
  list-style-type: none;
  padding-left: 0;
  margin-top: 1.5rem;
  margin-bottom: 2rem;
  text-align: left;
  font-size: 1.05rem;
}

.course-details-list li {
  margin-bottom: 0.75rem;
  position: relative;
  padding-left: 2rem;
}

.course-details-list li::before {
  content: "\f058"; /* FontAwesome check-circle icon */
  font-family: "FontAwesome";
  position: absolute;
  left: 0;
  color: var(--blue2);
  font-size: 1.2rem;
}

.course-details-list li strong {
  color: var(--blue2);
  font-weight: 600;
}

.course-details-list ul {
  list-style-type: none;
  padding-left: 2rem;
  margin-top: 0.5rem;
}

.course-details-list ul li {
  margin-bottom: 0.5rem;
}

.course-details-list ul li::before {
  content: "\f0da"; /* FontAwesome caret-right icon */
  font-family: "FontAwesome";
  font-size: 1rem;
  color: var(--green1);
}

/* Button styling */
.card-tag .btn {
  padding: 0.75rem 1.5rem;
  font-size: 1.05rem;
  font-weight: 600;
  border-radius: 30px;
  transition: all 0.3s ease;
}

.card-tag .btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
}
</style>
